import type { TaskStatus } from "@/types";
import type { ViewFilter } from "@/stores/useTaskFilterStore";
import { getIsoWeek, isoWeekToDateRange } from "@/lib/weekUtils";

export interface MemoTask {
  id: string;
  ownerUserId: string;
  title: string;
  description: string | null;
  status: TaskStatus;
  dueDate: string | null;
  startedAt: string | null;
  completedAt: string | null;
  version: number;
  createdByUserId: string | null;
  createdByName: string | null;
  viewerCanEdit: boolean;
}

export interface MemoList {
  ownerUserId: string;
  ownerName: string;
  totalCount: number;
  completedCount: number;
  inProgressCount: number;
  overdueCount: number;
}

export type MemoDeadlineChip = "all" | "overdue" | "thisWeek" | "nextWeek" | "noDeadline";

export const MEMO_DEADLINE_CHIPS: { value: MemoDeadlineChip; label: string }[] = [
  { value: "all", label: "Kaikki" },
  { value: "overdue", label: "Myöhässä" },
  { value: "thisWeek", label: "Tällä viikolla" },
  { value: "nextWeek", label: "Ensi viikolla" },
  { value: "noDeadline", label: "Ei määräaikaa" },
];

function toIsoDate(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function addDays(d: Date, days: number): Date {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  r.setDate(r.getDate() + days);
  return r;
}

export function isMemoTaskOverdue(
  task: Pick<MemoTask, "dueDate" | "status">,
  today: Date,
): boolean {
  if (!task.dueDate || task.status === "COMPLETED") return false;
  return task.dueDate < toIsoDate(today);
}

export function filterMemoTasks(
  tasks: MemoTask[],
  chip: MemoDeadlineChip,
  today: Date,
): MemoTask[] {
  switch (chip) {
    case "all":
      return tasks;
    case "noDeadline":
      return tasks.filter((t) => t.dueDate === null);
    case "overdue":
      return tasks.filter((t) => isMemoTaskOverdue(t, today));
    case "thisWeek":
    case "nextWeek": {
      const target = getIsoWeek(chip === "thisWeek" ? today : addDays(today, 7));
      const { start, endExclusive } = isoWeekToDateRange(target);
      const from = toIsoDate(start);
      const to = toIsoDate(endExclusive);
      return tasks.filter((t) => t.dueDate !== null && t.dueDate >= from && t.dueDate < to);
    }
  }
}

export function memoOwnerSelection(
  viewFilter: ViewFilter,
  currentUserId: string,
  assignedToFilter: string[],
  showOthers = true,
): string[] | null {
  if (!showOthers) {
    const selection = memoOwnerSelection(viewFilter, currentUserId, assignedToFilter);
    return selection === null || selection.includes(currentUserId) ? [currentUserId] : [];
  }
  if (viewFilter === "Minun") {
    if (assignedToFilter.length === 0) return [currentUserId];
    return assignedToFilter.includes(currentUserId) ? [currentUserId] : [];
  }
  return assignedToFilter.length === 0 ? null : assignedToFilter;
}
